import { useState, useRef, useCallback, useEffect, useMemo } from 'react';

// Minimum height allocation for ShowMoreCard to ensure button is always visible
// 28px button minHeight + 32px minimum content preview = 60px
const SHOW_MORE_MIN_HEIGHT = 80;

/**
 * Hook for coordinating height distribution between expandable cards
 * in DesktopLayout.
 *
 * @param {Object} options
 * @param {number} options.totalHeight - Total available height for left panel
 * @param {Object} options.product - Product object with detailsPre
 * @param {Object} options.resource - Resource object with description
 * @param {boolean} options.hasImage - Whether ResourceImage is displayed
 * @param {boolean} options.hasContact - Whether ContactCard is displayed
 * @param {boolean} options.hasResourceCard - Whether ResourceCard is displayed
 *
 * @returns {Object}
 * @returns {React.RefObject} returns.headerRef - Ref to attach to ResourceHeader wrapper
 * @returns {React.RefObject} returns.imageRef - Ref to attach to ResourceImage wrapper
 * @returns {React.RefObject} returns.contactRef - Ref to attach to ContactCard wrapper
 * @returns {Function} returns.registerProductCard - Callback for ProductCard to report dimensions
 * @returns {Function} returns.registerResourceCard - Callback for ResourceCard to report dimensions
 * @returns {number|null} returns.productCardHeight - Allocated height for ProductCard
 * @returns {number|null} returns.resourceCardHeight - Allocated height for ResourceCard
 * @returns {boolean} returns.productAutoExpand - Whether ProductCard should auto-expand
 * @returns {boolean} returns.resourceAutoExpand - Whether ResourceCard should auto-expand
 * @returns {Function} returns.setProductExpanded - Callback for ProductCard to report expansion state
 * @returns {Function} returns.setResourceExpanded - Callback for ResourceCard to report expansion state
 */
export default function useCardHeightDistribution({
  totalHeight,
  product,
  resource,
  hasImage,
  hasContact,
  hasResourceCard,
}) {
  // Refs for fixed elements
  const headerRef = useRef(null);
  const imageRef = useRef(null);
  const contactRef = useRef(null);

  // State for measured fixed heights
  const [fixedHeights, setFixedHeights] = useState({
    header: 0,
    image: 0,
    contact: 0,
  });

  // State for card dimensions (reported by cards)
  const [cardDimensions, setCardDimensions] = useState({
    product: { min: 0, max: 0 },
    resource: { min: 0, max: 0 },
  });

  // Callbacks for cards to report their dimensions
  const registerProductCard = useCallback(({ minHeight, maxHeight }) => {
    setCardDimensions((prev) => ({
      ...prev,
      product: { min: minHeight, max: maxHeight },
    }));
  }, []);

  const registerResourceCard = useCallback(({ minHeight, maxHeight }) => {
    setCardDimensions((prev) => ({
      ...prev,
      resource: { min: minHeight, max: maxHeight },
    }));
  }, []);

  // Measure fixed elements using ResizeObserver
  useEffect(() => {
    const measureElements = () => {
      setFixedHeights({
        header: headerRef.current?.getBoundingClientRect().height || 0,
        image: hasImage
          ? imageRef.current?.getBoundingClientRect().height || 0
          : 0,
        contact: hasContact
          ? contactRef.current?.getBoundingClientRect().height || 0
          : 0,
      });
    };

    // Use RAF to ensure elements are painted before measuring
    const rafId = requestAnimationFrame(measureElements);

    const ro = new ResizeObserver(measureElements);
    if (headerRef.current) ro.observe(headerRef.current);
    if (imageRef.current && hasImage) ro.observe(imageRef.current);
    if (contactRef.current && hasContact) ro.observe(contactRef.current);

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [hasImage, hasContact]);

  // Calculate distributed heights
  const distributedHeights = useMemo(() => {
    const GRID_SPACING = 8; // MUI Grid rowSpacing={1} = 8px
    const HEIGHT_EPSILON = 1; // 1px tolerance for all height comparisons

    // Count visible components to calculate spacing
    // Header is always present, ProductCard is always present
    let componentCount = 2; // header + productCard
    if (hasImage) componentCount++;
    if (hasResourceCard) componentCount++;
    if (hasContact) componentCount++;

    // Spacing between components (N components = N-1 gaps)
    const totalSpacing = (componentCount - 1) * GRID_SPACING;

    const fixedHeight =
      fixedHeights.header +
      fixedHeights.image +
      fixedHeights.contact +
      totalSpacing;

    const availableForCards = totalHeight - fixedHeight;

    const { product: pDim, resource: rDim } = cardDimensions;

    // If ProductCard hasn't reported dimensions yet, return null
    if (!pDim.min) {
      return {
        product: null,
        resource: null,
        productAutoExpand: false,
        resourceAutoExpand: false,
      };
    }

    // If no ResourceCard, give all space to ProductCard
    if (!hasResourceCard) {
      const productHasExpandableContent = Boolean(product?.detailsPre);
      const productMinWithShowMore =
        pDim.min + (productHasExpandableContent ? SHOW_MORE_MIN_HEIGHT : 0);
      // Ensure minimum height for button visibility, but cap at max
      const productHeight = Math.max(
        productMinWithShowMore,
        Math.min(availableForCards, pDim.max),
      );
      const productAutoExpand = productHeight >= pDim.max - HEIGHT_EPSILON;
      return {
        product: productHeight,
        resource: null,
        productAutoExpand,
        resourceAutoExpand: false,
      };
    }

    // If ResourceCard hasn't reported dimensions yet, return null
    if (!rDim.min) {
      return {
        product: null,
        resource: null,
        productAutoExpand: false,
        resourceAutoExpand: false,
      };
    }

    // Calculate minimum heights with ShowMoreCard space when content is expandable
    // This ensures the expand/collapse button is always visible
    const productHasExpandableContent = Boolean(product?.detailsPre);
    const resourceHasExpandableContent = Boolean(resource?.description);

    const productMinWithShowMore =
      pDim.min + (productHasExpandableContent ? SHOW_MORE_MIN_HEIGHT : 0);
    const resourceMinWithShowMore =
      rDim.min + (resourceHasExpandableContent ? SHOW_MORE_MIN_HEIGHT : 0);

    const minNeeded = productMinWithShowMore + resourceMinWithShowMore;

    // If insufficient space, both cards get minimum heights (may cause scrollbar)
    if (availableForCards <= minNeeded) {
      return {
        product: productMinWithShowMore,
        resource: resourceMinWithShowMore,
        productAutoExpand: false,
        resourceAutoExpand: false,
      };
    }

    const extraSpace = availableForCards - minNeeded;
    const productMaxExtra = pDim.max - pDim.min;
    const resourceMaxExtra = rDim.max - rDim.min;

    // Calculate weights based on content length
    const productContentLength = product?.detailsPre?.length || 0;
    const resourceContentLength = resource?.description?.length || 0;
    const totalContent = productContentLength + resourceContentLength;

    let productWeight = 0.5;
    let resourceWeight = 0.5;

    if (totalContent > 0) {
      productWeight = productContentLength / totalContent;
      resourceWeight = resourceContentLength / totalContent;
    }

    // Initial proportional distribution
    let productExtra = Math.min(extraSpace * productWeight, productMaxExtra);
    let resourceExtra = Math.min(extraSpace * resourceWeight, resourceMaxExtra);

    // Redistribute any remaining space with safety limit
    let remaining = extraSpace - productExtra - resourceExtra;
    const MAX_ITERATIONS = 10; // Safety limit to prevent infinite loops
    let iterations = 0;

    while (remaining > HEIGHT_EPSILON && iterations < MAX_ITERATIONS) {
      iterations++;
      const oldRemaining = remaining;

      if (productExtra < productMaxExtra) {
        const toAdd = Math.min(remaining, productMaxExtra - productExtra);
        productExtra += toAdd;
        remaining -= toAdd;
      }

      if (resourceExtra < resourceMaxExtra && remaining > 0) {
        const toAdd = Math.min(remaining, resourceMaxExtra - resourceExtra);
        resourceExtra += toAdd;
        remaining -= toAdd;
      }

      // Break if we couldn't distribute anything (both at max or float precision issue)
      if (Math.abs(remaining - oldRemaining) < HEIGHT_EPSILON) break;
    }

    const productHeight = productMinWithShowMore + productExtra;
    const resourceHeight = resourceMinWithShowMore + resourceExtra;

    // Determine if cards should auto-expand (height >= max content)
    const productAutoExpand = productHeight >= pDim.max - HEIGHT_EPSILON;
    const resourceAutoExpand = resourceHeight >= rDim.max - HEIGHT_EPSILON;

    return {
      product: productHeight,
      resource: resourceHeight,
      productAutoExpand,
      resourceAutoExpand,
    };
  }, [
    totalHeight,
    fixedHeights,
    cardDimensions,
    product,
    resource,
    hasImage,
    hasContact,
    hasResourceCard,
  ]);

  return {
    headerRef,
    imageRef,
    contactRef,
    registerProductCard,
    registerResourceCard,
    // Always return the calculated height - cards use this for ShowMoreCard's maxHeight
    // to determine whether to show the expand/collapse button.
    // Cards manage their own expanded state internally.
    productCardHeight: distributedHeights.product,
    resourceCardHeight: distributedHeights.resource,
    productAutoExpand: distributedHeights.productAutoExpand || false,
    resourceAutoExpand: distributedHeights.resourceAutoExpand || false,
  };
}
